import axios from 'axios'
import { API_URL, AUTH, PROFILE, RETOKEN, TOKEN } from '../Constants'
import SwalErrorToken from './swal-error-token'
import SwalErrorCustom from './swal-error'
import { useLanguageStage } from '../store/layout'

const apiInstances = {}

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error)
    else prom.resolve(token)
  })
  failedQueue = []
}

const apiSet = (version = 'v1') => {
  const api = axios.create({
    baseURL: `${API_URL}api/${version}/`,
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v))
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, value)
        }
      })
      return searchParams.toString()
    }
  })

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(TOKEN)
      const lang = useLanguageStage.getState().languageStage ?? 'en'

      config.headers['Accept-Language'] = lang

      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    },
    (error) => {
      return SwalErrorCustom(error)
    }
  )

  api.interceptors.response.use(
    (res) => {
      // A blob response (file download) needs `res.headers` too (e.g. content-disposition for the
      // filename), so it's returned whole instead of unwrapped to `res.data` like every other call.
      return res.config?.responseType === 'blob' ? res : res.data
    },
    async (err) => {
      const originalConfig = err.config

      if (err.response) {
        if (err.response.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true

          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject })
            }).then((newToken) => {
              originalConfig.headers['Authorization'] = 'Bearer ' + newToken
              return api(originalConfig)
            })
          }
          isRefreshing = true

          try {
            const res = await getRefreshToken()
            if (!res) return
            if (res.status) {
              api.defaults.headers.common['Authorization'] =
                `Bearer ${res.accessToken}`

              processQueue(null, res.accessToken)

              originalConfig.headers['Authorization'] =
                `Bearer ${res.accessToken}`

              localStorage.setItem(RETOKEN, res.refreshToken)
              localStorage.setItem(TOKEN, res.accessToken)
              localStorage.setItem(PROFILE, JSON.stringify(res.result[0]))
            } else {
              SwalErrorToken()
            }
            return api(originalConfig)
          } catch (_error) {
            processQueue(_error, null)
            return SwalErrorCustom(_error)
          }
        } else if (err.response.status === 403) {
          processQueue(err, null)

          return SwalErrorToken()
        } else {
          processQueue(err, null)

          SwalErrorCustom(err)
        }
      } else {
        return
      }
      return
    }
  )

  async function getRefreshToken() {
    const retoken = localStorage.getItem(RETOKEN)
    return await postJwt('v1/refreshToken', {
      refreshToken: retoken
    })
  }
  const postJwt = async (path, body = {}) => {
    return await axios
      .post(`${API_URL}api/${path}`, body, {
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json',
          authorization: AUTH
        }
      })
      .then((response) => {
        return response.data
      })
      .catch(() => {
        SwalErrorToken()
      })
  }
  return api
}

const API = (version = 'v1') => {
  if (!apiInstances[version]) {
    apiInstances[version] = apiSet(version)
  }
  return apiInstances[version]
}

export const post = async (path, data, version = 'v1') => {
  return await API(version).post(path, data)
}

export const get = async (path, params = {}, headers = {}, version = 'v1') => {
  return await API(version).get(path, { params, headers })
}

export const getV2 = async (path, params = {}, headers = {}, version = 'v2') => {
  return await API(version).get(path, { params, headers })
}

export const put = async (path, params = {}, data = {}, version = 'v1') => {
  return await API(version).put(path, data, { params })
}

export const del = async (path, params = {}, data = {}, version = 'v1') => {
  return await API(version).delete(path, { params, data })
}

// Goes through the shared `api` instance (not a standalone axios.get) so a blob download gets the
// same Authorization/Accept-Language headers, array-valued paramsSerializer, and 401-refresh retry
// as every other call — a raw axios.get here previously skipped all three.
export const getBlob = async (path, params = {}, version = 'v1') => {
  return await API(version).get(path, { params, responseType: 'blob' })
}
